/**
 * [sc-48351 / task 3] La veste di CRM PLUS.
 *
 * Le pagine sono le stesse di START: cambia la cornice (sidebar e barra
 * sottile, da crmplus-web) e, sotto la classe `layout-plus`, l'aspetto. Qui
 * stanno le regole, pure e provabili; le usano `App.vue` e i componenti in
 * `components/plus`.
 */
import { categoryLabel, listiniCategories } from "@/config/listiniCategories";
import { isPlatformAdmin } from "@/utilities/platformGate";

/** La classe che accende la veste. Va su `<html>`: le modali stanno fuori da #app. */
export const CLASSE_LAYOUT_PLUS = "layout-plus";

/** Pagine che restano nella veste di sempre anche per un'azienda PLUS. */
const PERCORSI_SENZA_CORNICE = ["/login", "/registrazione", "/configurazione"];

/**
 * La cornice PLUS vale per chi è dentro il gestionale di un'azienda PLUS.
 * Fuori restano la console di piattaforma, l'accesso, la registrazione e la
 * configurazione guidata, che non sono il gestionale.
 */
export function usaLayoutPlus({ plan, currentUser, route } = {}) {
  if (plan !== "PLUS" || !currentUser) return false;
  if (isPlatformAdmin(currentUser)) return false;
  const meta = route?.meta || {};
  if (meta.platformBrand || meta.platformOnly) return false;
  const path = route?.path || "";
  return !PERCORSI_SENZA_CORNICE.some(
    (p) => path === p || path.startsWith(`${p}/`)
  );
}

/**
 * Le voci di navigazione: le stesse schede della home di START, nello stesso
 * ordine. Ognuna ha la sua area: compare a chi la può vedere.
 *
 * [sc-48351 / task 48786] Le voci seguono il ruolo. Senza `can` (chi chiama
 * ancora col solo isAdmin) vale la regola di prima: clienti, preventivi e
 * ordini solo agli amministratori.
 */
export function vociSidebar({
  isAdmin = false,
  can = null,
  features = {},
} = {}) {
  const vede =
    can ??
    ((area) => isAdmin || ["dashboard", "listini", "magazzino"].includes(area));
  return [
    {
      to: "/home",
      etichetta: "Home",
      icona: "fa-house",
      esatta: true,
      area: "dashboard",
    },
    {
      to: "/home/clienti",
      etichetta: "Clienti",
      icona: "fa-users",
      area: "clienti",
    },
    {
      to: "/home/preventivi",
      etichetta: "Preventivi",
      icona: "fa-file-invoice",
      area: "preventivi",
    },
    {
      to: "/home/ordini",
      etichetta: "Ordini",
      icona: "fa-clipboard-list",
      area: "ordini",
    },
    // [sc-49222] L'area delle fatture elettroniche: solo con la funzione
    // accesa per l'azienda e per chi può vedere le fatture.
    {
      to: "/home/fatturazione",
      etichetta: "Fatturazione",
      icona: "fa-file-invoice-dollar",
      area: "fatture",
      feature: "invoicing",
    },
    {
      to: "/home/magazzino",
      etichetta: "Magazzino",
      icona: "fa-warehouse",
      area: "magazzino",
    },
    // Le schede dei listini si aprono come sottomenu, una per categoria.
    {
      to: "/home/listini",
      etichetta: "Listini",
      icona: "fa-tags",
      categorie: true,
      area: "listini",
    },
  ].filter(
    (voce) =>
      vede(voce.area, "V") &&
      (!voce.feature || Boolean(features?.[voce.feature]))
  );
}

/**
 * [sc-48351 / task 48786] Dove va chi non vede la dashboard: la prima area
 * della sidebar che può aprire, e se non ne ha nessuna l'Account, che vedono
 * tutti.
 */
export function primaPaginaVisibile(can) {
  if (can("dashboard", "V")) return "/home";
  const voce = vociSidebar({ can }).find((v) => v.area !== "dashboard");
  return voce ? voce.to : "/home/account";
}

/**
 * Le voci in fondo alla sidebar: le pagine che si aprono di rado. Sono quelle
 * di Impostazioni nella barra di START, solo per gli amministratori. Account
 * si apre dal nome nella barra in alto.
 */
export function vociImpostazioni({ isAdmin = false, features = {} } = {}) {
  if (!isAdmin) return [];
  const voci = [
    {
      to: "/home/marchio",
      etichetta: "Configurazione azienda",
      icona: "fa-palette",
    },
    { to: "/home/dati", etichetta: "I tuoi dati", icona: "fa-download" },
  ];
  // [sc-48351 / task 48782] Il log lo **scrivono** tutte le aziende; la
  // pagina che lo mostra è di PLUS, e la voce compare solo se il piano
  // l'accende — altrimenti porterebbe a una pagina che l'API rifiuta.
  if (features.activityLog) {
    voci.push({
      to: "/home/log",
      etichetta: "Log attività",
      icona: "fa-clock-rotate-left",
    });
  }
  // [sc-48351 / task 48786] I ruoli sono di PLUS, e li gestisce solo
  // l'amministratore.
  if (features.roles) {
    voci.push({
      to: "/home/ruoli",
      etichetta: "Ruoli",
      icona: "fa-user-shield",
    });
  }
  return voci;
}

/**
 * Il percorso della pagina, come lo mostra la barra. Stessa regola della
 * barra di START: il nome dal `meta.breadcrumb`, e per una scheda dei listini
 * l'etichetta della categoria invece dello slug.
 */
export function breadcrumbsFor(route, productCategories) {
  const params = route?.params || {};
  return (route?.matched || []).map((matched) => {
    let path = matched.path;
    for (const [key, value] of Object.entries(params)) {
      path = path.replace(`:${key}`, value);
    }
    let name = matched.meta?.breadcrumb || matched.name;
    if (matched.name === "listini-category" && params.category) {
      name = categoryLabel(params.category, productCategories);
    }
    return { name, path };
  });
}

/**
 * Come si dice «torna» a ogni sezione.
 *
 * [sc-48951] La barra non elenca più dove sei: ogni livello sopra quello
 * aperto diventa il modo per uscirne. «Torna» vuole la preposizione
 * articolata, e in italiano non si ricava dal nome — «ai preventivi» ma «al
 * magazzino», «alla Home» ma «agli ordini». Quindi una tabella, con il
 * ripiego per i nomi che non ci sono: le categorie dei listini, che arrivano
 * dalla configurazione del cliente e non si possono elencare qui.
 */
const RITORNI = Object.freeze({
  Home: "Torna alla Home",
  Clienti: "Torna ai clienti",
  Preventivi: "Torna ai preventivi",
  Ordini: "Torna agli ordini",
  Fatturazione: "Torna alla fatturazione",
  Magazzino: "Torna al magazzino",
  Listini: "Torna ai listini",
  Account: "Torna all'account",
  Azienda: "Torna all'azienda",
  Registro: "Torna al registro",
  Piattaforma: "Torna alla piattaforma",
  "I tuoi dati": "Torna ai tuoi dati",
  "Log attività": "Torna al log attività",
  Ruoli: "Torna ai ruoli",
  "Carico Merce": "Torna al carico merce",
  "Configurazione azienda": "Torna alla configurazione",
  "Variazioni di prezzo": "Torna alle variazioni di prezzo",
});

export function etichettaRitorno(nome) {
  if (!nome) return "Torna indietro";
  return RITORNI[nome] || `Torna a ${nome}`;
}

/**
 * Il sottomenu dei Listini: prima tutte le schede, poi una voce per categoria.
 * Le categorie sono le stesse della pagina dei listini, comprese quelle che
 * compaiono perché contengono articoli.
 */
export function vociListini(
  productCategories,
  itemCategoriesInUse,
  { isAdmin = false, features = {} } = {}
) {
  const voci = [
    {
      to: "/home/listini",
      etichetta: "Tutti i listini",
      icona: "fa-solid fa-tags",
      esatta: true,
    },
    ...listiniCategories(productCategories, itemCategoriesInUse).map((cat) => ({
      to: `/home/listini/${cat.slug}`,
      etichetta: cat.label,
      icona: cat.icon,
    })),
  ];
  // [sc-48351 / task 48783] In fondo, dopo le schede: cambiare i prezzi di
  // molti articoli si fa di rado, e non è una scheda da sfogliare. Solo agli
  // amministratori, e solo col piano che l'accende — altrimenti la voce
  // porterebbe a una pagina che l'API rifiuta.
  if (isAdmin && features.priceListAdjustments) {
    voci.push({
      to: "/home/listini/variazioni",
      etichetta: "Variazioni di prezzo",
      icona: "fa-solid fa-arrow-up-right-dots",
      esatta: true,
    });
  }
  return voci;
}
